"use client";

import React, { useState } from "react";
import { 
  X, 
  Calendar, 
  Clock, 
  Users, 
  Utensils, 
  Sparkles, 
  CheckCircle2, 
  Wine, 
  AlertCircle,
  ArrowRight,
  ShieldCheck
} from "lucide-react";
import { DINING_TIME_SLOTS, DINING_AREAS, DINING_OCCASIONS, TASTING_MENUS } from "@/lib/demos/restaurantData";

interface TableReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: "INR" | "USD";
  initialMenuId?: string;
  initialSalonId?: string;
  onReservationCreated?: (res: any) => void;
}

export default function TableReservationModal({
  isOpen,
  onClose,
  currency,
  initialMenuId,
  initialSalonId,
  onReservationCreated,
}: TableReservationModalProps) {
  const selectedMenuDefault = TASTING_MENUS.find(m => m.id === initialMenuId)?.name || "The 7-Course Signature Degustation";

  const [formData, setFormData] = useState({
    guest_name: "",
    email: "",
    phone: "",
    party_size: 2,
    reservation_date: new Date(Date.now() + 86400000 * 2).toISOString().split("T")[0],
    time_slot: DINING_TIME_SLOTS[2], // 06:30 PM
    dining_area: DINING_AREAS[0],
    occasion: DINING_OCCASIONS[0],
    tasting_menu: selectedMenuDefault,
    dietary_notes: "",
    special_requests: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [createdReservation, setCreatedReservation] = useState<any>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await fetch("/api/demos/restaurant/reservations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          currency,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to confirm table reservation");
      }

      setCreatedReservation(data.reservation);
      if (onReservationCreated) onReservationCreated(data.reservation);
    } catch (err: any) {
      setErrorMessage(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetModal = () => {
    setCreatedReservation(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-2xl bg-stone-950 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 bg-stone-900/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Utensils className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">Reserve a Table at L'Aura</h3>
              <p className="text-xs text-stone-400">Curated Gastronomy, Cellar Flights & Table Allocation</p>
            </div>
          </div>

          <button
            onClick={handleResetModal}
            className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {createdReservation ? (
            /* Success State */
            <div className="text-center py-6 space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div className="space-y-2">
                <span className="text-xs uppercase tracking-widest font-bold text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                  Table Intake Registered
                </span>
                <h4 className="text-2xl font-bold text-white">Reservation Confirmed</h4>
                <p className="text-sm text-stone-300 max-w-md mx-auto">
                  Thank you, <strong>{createdReservation.guest_name}</strong>. An official dining voucher has been dispatched to <strong>{createdReservation.email}</strong> via Hostinger SMTP.
                </p>
              </div>

              {/* Reservation Receipt Card */}
              <div className="bg-stone-900/90 border border-stone-800 rounded-xl p-5 text-left max-w-lg mx-auto space-y-3 shadow-inner">
                <div className="flex justify-between items-center border-b border-stone-800 pb-2">
                  <span className="text-xs font-semibold text-stone-400">Booking Reference</span>
                  <span className="text-xs font-mono font-bold text-amber-400">#RES-{createdReservation.id}-LA</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-stone-400 block">Date & Sitting</span>
                    <span className="text-white font-semibold">{createdReservation.reservation_date} • {createdReservation.time_slot}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Party Size</span>
                    <span className="text-white font-semibold">{createdReservation.party_size} Guests ({createdReservation.occasion})</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Dining Salon</span>
                    <span className="text-amber-300 font-semibold">{createdReservation.dining_area}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Tasting Menu</span>
                    <span className="text-white font-semibold">{createdReservation.tasting_menu}</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-stone-800 flex items-center justify-between text-xs text-stone-400">
                  <span className="flex items-center gap-1 text-emerald-400 font-medium">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Real-time Floor CRM Sync Active
                  </span>
                  <span>Host: {createdReservation.assigned_sommelier}</span>
                </div>
              </div>

              <div className="pt-3 flex flex-wrap justify-center gap-3">
                <a
                  href="/demos/restaurant/crm"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-semibold text-sm shadow-lg shadow-amber-600/30 transition-all hover:scale-105"
                >
                  <span>Open Restaurant Floor & Reservation CRM</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={handleResetModal}
                  className="px-4 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 font-medium text-sm border border-stone-800 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* Reservation Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Guest Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Guest Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Lord Alistair & Lady Vance"
                    value={formData.guest_name}
                    onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Email Address * (For Dining Voucher)
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. alistair@vance-holdings.co.uk"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Phone / WhatsApp (Optional)
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. +91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Party Size (Number of Guests) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={20}
                      required
                      value={formData.party_size}
                      onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value, 10) || 1 })}
                      className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                    <Users className="w-4 h-4 text-stone-500 absolute right-3.5 top-3 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Date & Sitting */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Reservation Date *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      required
                      min={new Date().toISOString().split("T")[0]}
                      value={formData.reservation_date}
                      onChange={(e) => setFormData({ ...formData, reservation_date: e.target.value })}
                      className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                    />
                    <Calendar className="w-4 h-4 text-stone-500 absolute right-3.5 top-3 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Sitting / Time Slot *
                  </label>
                  <select
                    value={formData.time_slot}
                    onChange={(e) => setFormData({ ...formData, time_slot: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  >
                    {DINING_TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dining Salon & Menu */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Preferred Dining Area / Salon *
                  </label>
                  <select
                    value={formData.dining_area}
                    onChange={(e) => setFormData({ ...formData, dining_area: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  >
                    {DINING_AREAS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Tasting Menu Selection *
                  </label>
                  <select
                    value={formData.tasting_menu}
                    onChange={(e) => setFormData({ ...formData, tasting_menu: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  >
                    {TASTING_MENUS.map((m) => (
                      <option key={m.id} value={m.name}>
                        {m.name} ({currency === "INR" ? m.priceINR : m.priceUSD})
                      </option>
                    ))}
                    <option value="A La Carte & Vintage Wine Cellar">A La Carte & Vintage Wine Cellar</option>
                  </select>
                </div>
              </div>

              {/* Occasion & Dietary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Dining Occasion
                  </label>
                  <select
                    value={formData.occasion}
                    onChange={(e) => setFormData({ ...formData, occasion: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  >
                    {DINING_OCCASIONS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Dietary Restrictions / Allergies
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1 Pescatarian, 1 Nut allergy, Grand Cru flight"
                    value={formData.dietary_notes}
                    onChange={(e) => setFormData({ ...formData, dietary_notes: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Special Table Requests / Notes for Maître d'
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Quiet booth, celebration flowers, or specific vintage decanting on arrival."
                  value={formData.special_requests}
                  onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              {/* Assurance Banner */}
              <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl flex items-center justify-between text-xs text-stone-300">
                <div className="flex items-center gap-2">
                  <Wine className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>Complimentary valet parking & cellar preview included with every reservation.</span>
                </div>
              </div>

              {/* Submit CTA */}
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 font-medium text-xs border border-stone-800 transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-500 text-white font-bold text-sm shadow-lg shadow-amber-600/30 transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Confirming Table...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-amber-200" />
                      <span>Confirm Table Reservation</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
